import { type HTMLAttributes, type ReactNode } from 'react';
import { cn } from '@/lib/utils.ts';
import { SectionLabel } from '@/components/ui/Title.tsx';

/**
 * Lista pozycji ustawień — szablony, zgody, kategorie, tagi, konta bankowe.
 * Cztery ekrany rysowały ten sam kontener na cztery sposoby (raz `divide-y`,
 * raz kafle `Card`, raz `<ul>` z ramką na wierszu, raz siatka o stałych
 * kolumnach, która rozjeżdżała się na telefonie).
 */
const SettingsList = ({
  className,
  ...props
}: HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      'divide-y divide-neutral-100 overflow-hidden rounded-[10px] border border-neutral-200',
      className,
    )}
    {...props}
  />
);
SettingsList.displayName = 'SettingsList';

interface SettingsListRowProps
  extends Omit<HTMLAttributes<HTMLDivElement>, 'title'> {
  /** Znak przed nazwą: obręcz koloru, chip tagu, plakietka waluty. */
  leading?: ReactNode;
  title: ReactNode;
  /** Plakietka tuż za nazwą — typ zgody, „Domyślne”. */
  badge?: ReactNode;
  /** Stan rekordu za plakietką — weryfikacja TPay, archiwizacja. */
  status?: ReactNode;
  subtitle?: ReactNode;
  actions?: ReactNode;
  /** Pozycja wycofana z użycia — wygasza treść, nie akcje. */
  muted?: boolean;
}

const SettingsListRow = ({
  leading,
  title,
  badge,
  status,
  subtitle,
  actions,
  muted = false,
  className,
  ...props
}: SettingsListRowProps) => (
  <div
    className={cn('flex items-center gap-3 px-3.5 py-[11px]', className)}
    {...props}
  >
    {leading}
    <div className={cn('min-w-0 flex-1', muted && 'opacity-60')}>
      <div className="flex flex-wrap items-center gap-2">
        {/* Na telefonie nazwa się zawija — ucięta w połowie nic nie mówi. */}
        <span className="break-words text-sm font-semibold text-ink md:truncate">
          {title}
        </span>
        {badge}
        {status}
      </div>
      {subtitle && (
        <p className="mt-0.5 text-[12.5px] leading-[1.45] text-neutral-600">
          {subtitle}
        </p>
      )}
    </div>
    {actions && (
      <div className="flex shrink-0 items-center gap-1.5">{actions}</div>
    )}
  </div>
);
SettingsListRow.displayName = 'SettingsListRow';

/** Nagłówek grupy nad wydzieloną częścią listy — „Zarchiwizowane”. */
const SettingsListGroupLabel = ({ children }: { children: ReactNode }) => (
  <div className="flex items-center gap-2.5">
    <SectionLabel>{children}</SectionLabel>
    <span className="h-px flex-1 bg-neutral-100" />
  </div>
);
SettingsListGroupLabel.displayName = 'SettingsListGroupLabel';

export { SettingsList, SettingsListRow, SettingsListGroupLabel };
